import { LitElement, html, css, nothing } from 'lit';
import { generationChanged } from '../utils/constants.js';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, badgeStyles } from '../styles/shared.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import type { DocumentController } from '../controllers/document-controller.js';
import type { ViewDefinition } from '../api/types.js';
import './sysml-view-selector.js';

// ---------------------------------------------------------------------------
// <sysml-toolbar> — Simplified header bar (V2 layout)
//
// Kept: Doc name + dirty indicator, parse status, view selector,
//       diagram type buttons, auto layout, format, copy, download, save
// Removed: Explorer toggle, search/diagnostics toggles, cursor position,
//          theme toggle, focus editor/diagram, fullscreen, right panel toggle
//          (all moved to activity bar, status bar, or keyboard shortcuts)
// ---------------------------------------------------------------------------

/** Detect macOS for keyboard shortcut display */
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const MOD = IS_MAC ? 'Cmd' : 'Ctrl';

@customElement('sysml-toolbar')
export class SysmlToolbar extends LitElement {
  static override styles = [
    baseStyles,
    badgeStyles,
    css`
      :host {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        background: var(--r5-bg, var(--r5-surface));
        flex-shrink: 0;
        min-height: 36px;
      }
      .left { display: flex; align-items: center; gap: var(--r5-space-2); flex: 1; min-width: 0; }
      .app-logo { flex-shrink: 0; width: 24px; height: 24px; }
      .right { display: flex; align-items: center; gap: var(--r5-space-1); flex-shrink: 0; }
      .doc-name {
        font-weight: 600;
        font-size: var(--r5-text-xs);
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .dirty-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--r5-warning-500);
        flex-shrink: 0;
      }
      .separator {
        width: 1px;
        height: 16px;
        background: var(--r5-border);
        flex-shrink: 0;
        margin: 0 var(--r5-space-1);
      }
      .btn-group {
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 2px;
        border-radius: var(--r5-radius-md);
        background: color-mix(in srgb, var(--r5-text) 4%, transparent);
      }
      .tb-active {
        background: color-mix(in srgb, var(--r5-primary) 12%, transparent);
        border-radius: var(--r5-radius-sm);
      }
      .layout-select {
        font-size: var(--r5-text-xs);
        padding: 2px 4px;
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-sm);
        background: var(--r5-surface);
        color: var(--r5-text);
        cursor: pointer;
        height: 24px;
      }
      .layout-select:focus {
        outline: 2px solid var(--r5-primary);
        outline-offset: -1px;
      }
      .export-menu {
        position: relative;
        display: inline-flex;
      }
      .export-dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        z-index: 20;
        min-width: 130px;
        background: var(--r5-surface-overlay, var(--r5-surface));
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        box-shadow: var(--r5-shadow-lg, 0 8px 24px rgba(0,0,0,.25));
        padding: var(--r5-space-1) 0;
        margin-top: 2px;
      }
      .export-dropdown-item {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) var(--r5-space-3);
        font-size: var(--r5-text-xs);
        color: var(--r5-text);
        cursor: pointer;
        white-space: nowrap;
        background: none;
        border: none;
        width: 100%;
        text-align: left;
      }
      .export-dropdown-item:hover {
        background: color-mix(in srgb, var(--r5-primary) 12%, transparent);
      }
    `,
  ];

  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;
  @property({ attribute: false, hasChanged: generationChanged }) documents!: DocumentController;
  @property({ attribute: false }) diagramView: 'diagram' | 'ibd' | 'sequence' | 'statemachine' | 'activity' | 'package' | 'requirement' | 'usecase' | 'parametric' = 'diagram';
  @property({ type: Boolean, attribute: 'diagram-visible' }) diagramVisible = true;
  @state() private _copiedFeedback = false;
  @state() private _exportMenuOpen = false;

  override render() {
    const m = this.modeler;
    const d = this.documents;

    return html`
      <div class="left">
        <svg class="app-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 250 250" fill="none">
          <path d="M 21.47,77.61 V 53.86 C 21.47,49.35 24.81,47.62 27.62,47.62 H 241 V 82.91 H 188.87 V 200.18 H 220.71 C 225.88,200.18 227.82,196.11 227.82,193.89 V 132.09" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 21.47,111.68 V 158.94" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 59.96,47.62 V 25.12 C 59.96,20.71 62.96,18.5 66.37,18.5 H 189.09 C 193.19,18.5 195.16,21.17 195.16,24.97 V 47.62" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 82.91,47.62 V 40.71 C 82.91,36.91 85.51,33.81 88.81,33.81 H 166.1" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 112.31,28.08 H 144.47 V 38.82 H 112.31 V 28.08 Z" fill="#F7B04A" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 112.31,28.08 H 122.12 V 38.82 H 112.31 V 28.08 Z" fill="#4CB6DF" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 172.31,40.09 C 177.09,41.26 180.16,37.17 178.78,32.05 C 177.96,28.87 175.17,27.36 172.31,27.75 C 168.76,28.23 166.1,31.81 166.73,35.53 C 167.2,38.28 169.5,39.43 172.31,40.09 Z" fill="#4CB6DF" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 195.99,40.09 C 200.77,40.09 202.39,35.99 201.45,32.73 C 200.39,28.98 197.51,27.75 195.16,28.08 C 191.01,28.67 189.72,32.05 189.96,34.72 C 190.26,37.89 192.61,40.09 195.99,40.09 Z" fill="#F18D52" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 104.08,47.86 V 82.91 H 155.21 V 200.18 H 188.32 V 82.91 H 241 V 47.86 H 104.08 Z" fill="url(#paint0_linear_101_111)" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 8.18,200.42 L 59.31,48.21 H 104.08 V 65.76 H 70.64 L 59.42,98.21 L 57.31,109.06 L 32.92,177.72 L 31.98,187.91 H 36.01 L 46.41,199.94 H 8.18 V 200.42 Z" fill="url(#paint1_linear_101_111)" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 46.18,143.26 L 76.25,143.03 L 79.71,141.65 L 86.22,142.21 L 135.86,142.79 L 150.97,184.76 L 154.78,200.18 H 119.71 L 106.61,160.32 H 58.24 L 45.14,199.94" fill="url(#paint2_linear_101_111)"/>
          <path d="M 46.18,143.26 L 76.25,143.03 L 79.71,141.65 L 86.22,142.21 L 135.86,142.79 L 150.97,184.76 L 154.78,200.18 H 119.71 L 106.61,160.32 H 58.24 L 45.14,199.94" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 104.08,65.76 H 70.64 L 59.42,98.21" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 137.21,142.79 L 122.23,103.11 L 119.63,82.91" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 110.61,103.11 H 132.98" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 108.01,82.91 H 155.21 V 142.79" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 130.25,65.3 H 213.71" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 122.23,71.1 C 126.86,70.62 127.72,66.71 127.01,63.96 C 126.07,60.24 123.26,58.9 120.66,59.14 C 116.74,59.5 115.61,63.01 115.84,65.76 C 116.15,68.9 118.5,71.1 122.23,71.1 Z" fill="#B1E1F2" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 222.61,71.1 C 227.24,70.62 228.1,66.71 227.39,63.96 C 226.45,60.24 223.64,58.9 221.04,59.14 C 217.12,59.5 215.99,63.01 216.22,65.76 C 216.53,68.9 218.88,71.1 222.61,71.1 Z" fill="#B1E1F2" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 172.19,66.12 V 109.06" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 171.62,120.73 C 176.25,121.76 178.52,117.86 177.73,114.41 C 176.87,110.51 173.72,109.38 171.12,109.85 C 167.67,110.51 165.97,113.41 166.1,116.08 C 166.33,118.83 168.52,120.21 171.62,120.73 Z" fill="#D7EAF2" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 139.11,109.06 C 143.74,108.58 144.95,105.41 144.64,102.39 C 144.17,98.33 141.21,97.07 138.61,97.43 C 134.69,97.89 133.11,101.06 133.42,103.72 C 133.72,106.99 135.91,108.82 139.11,109.06 Z" fill="#F7B04A" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 106.46,108.7 C 110.76,108.22 112.26,104.78 111.79,101.88 C 111.08,97.81 108.12,97.07 105.85,97.43 C 101.93,97.9 100.35,100.75 100.58,103.72 C 100.81,106.99 103.16,108.7 106.46,108.7 Z" fill="#F7A248" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 58.71,109.06 C 63.34,108.58 64.84,105.41 64.37,102.39 C 63.66,98.33 60.7,97.07 58.1,97.43 C 54.18,97.89 52.97,101.06 53.05,103.72 C 53.12,106.99 55.51,108.82 58.71,109.06 Z" fill="#F79A4D" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 119.63,142.79 L 108.01,108.7" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 82.91,82.91 L 67.51,127.29 H 96.72 L 82.91,82.91 Z" fill="#FEFFFE" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 82.91,149.38 C 87.54,149.38 88.75,145.94 88.36,143.27 C 87.7,139.21 85.1,137.4 82.5,137.75 C 78.58,138.22 77.08,140.62 76.92,143.62 C 76.77,146.89 79.27,149.38 82.91,149.38 Z" fill="#F48657" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 32.37,187.91 C 37.01,188.38 38.14,185.21 38.14,182.55 C 38.14,178.92 35.26,176.71 32.66,177.07 C 28.74,177.66 27.45,179.99 27.45,182.66 C 27.45,185.82 29.56,187.67 32.37,187.91 Z" fill="#F46D59" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 171.62,149.38 C 176.25,149.85 177.96,146.13 177.73,143.27 C 177.34,139.21 174.46,137.75 171.86,137.99 C 167.94,138.34 166.1,141.29 166.1,143.96 C 166.1,147.23 168.52,148.94 171.62,149.38 Z" fill="#D7EAF2" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 133.42,189.62 C 138.05,189.15 139.11,185.98 138.64,183.21 C 137.93,179.15 134.97,178.26 132.37,178.61 C 128.45,179.19 127.31,182.04 127.55,184.7 C 127.86,187.87 130.21,189.62 133.42,189.62 Z" fill="#F47D52" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 171.62,172.32 C 176.25,171.85 177.73,168.68 177.26,165.91 C 176.32,162.19 173.72,160.85 171.12,161.21 C 167.2,161.68 165.97,164.85 166.1,167.51 C 166.33,170.68 168.52,172.08 171.62,172.32 Z" fill="#D7EAF2" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 137.21,184.76 H 171.12 V 172.32" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 155.21,142.79 H 166.1" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 135.86,142.79 H 153.92" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 189.26,178.61 H 198.53" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 205.64,173.41 V 153.68" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 206.93,183.92 C 211.56,183.45 211.79,179.55 211.25,176.79 C 210.19,173.41 207.38,172.51 205.22,173.09 C 201.3,174.22 200.01,176.71 200.31,179.38 C 200.62,182.13 203.14,184.11 206.93,183.92 Z" fill="#F47D52" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 200.01,121.43 H 210.65 V 153.68 H 200.01 V 121.43 Z" fill="url(#tbl0)" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 200.01,133.87 H 210.65" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 222.14,127.64 H 233.44 V 148.35 H 222.14 V 127.64 Z" fill="url(#tbl1)" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 227.82,90.71 V 127.64" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 205.64,90.71 V 121.43" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 200.31,83.81 H 210.65 V 90.71 H 200.31 V 83.81 Z" fill="#F7A248" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 222.61,83.81 H 232.95 V 90.71 H 222.61 V 83.81 Z" fill="#F7A248" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 16.31,77.61 H 27.45 V 111.21 H 16.31 V 77.61 Z" fill="url(#tbl2)" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 16.31,88.92 H 27.45" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 188.32,200.18 V 223.61 C 188.32,228.13 185.22,229.5 182.87,229.5 H 73.86 C 69.08,229.5 67.21,226.05 67.21,223.29 V 185.39" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 162.72,201.64 V 209.44 C 162.72,213.86 159.62,215.7 157.27,215.7 H 140.7" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 134.82,221.5 C 139.45,221.03 140.31,217.58 139.6,214.83 C 138.66,211.11 135.85,209.77 133.25,210.12 C 129.33,210.59 128.12,213.76 128.28,216.43 C 128.51,219.29 130.94,221.5 134.82,221.5 Z" fill="#F48657" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 128.28,215.7 H 88.73 C 84.66,215.7 82.72,212.08 82.72,209.32 V 161.45" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 97.38,161.45 V 173.41" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 97.38,184.76 C 102.01,184.28 103.51,180.84 102.72,177.82 C 101.59,173.76 98.63,172.95 96.46,173.41 C 92.54,174.22 91.33,177.07 91.56,179.73 C 91.87,182.9 93.73,184.76 97.38,184.76 Z" fill="#F48657" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 66.98,161.45 V 172.97" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M 66.98,184.76 C 71.61,184.76 73.11,181.32 72.64,178.57 C 71.7,174.85 68.82,173.51 66.65,173.98 C 62.73,174.89 61.44,177.07 61.75,179.73 C 62.06,182.9 63.77,184.52 66.98,184.76 Z" fill="#4CB6DF" stroke="#183A63" stroke-width="3" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
          <defs>
            <linearGradient id="paint0_linear_101_111" x1="172.541" y1="47.8555" x2="172.541" y2="200.18" gradientUnits="userSpaceOnUse">
              <stop stop-color="#F7B04A"/>
              <stop offset="1" stop-color="#F46251"/>
            </linearGradient>
            <linearGradient id="paint1_linear_101_111" x1="56.1301" y1="48.2109" x2="56.1301" y2="200.418" gradientUnits="userSpaceOnUse">
              <stop stop-color="#49CAE5"/>
              <stop offset="1" stop-color="#3E68B8"/>
            </linearGradient>
            <linearGradient id="paint2_linear_101_111" x1="100.477" y1="141.646" x2="100.477" y2="200.18" gradientUnits="userSpaceOnUse">
              <stop stop-color="#3EA6D6"/>
              <stop offset="1" stop-color="#3E68B8"/>
            </linearGradient>
            <linearGradient id="tbl0" x1="205.333" y1="121.43" x2="205.333" y2="153.679" gradientUnits="userSpaceOnUse">
              <stop stop-color="#49CAE5"/>
              <stop offset="1" stop-color="#3E68B8"/>
            </linearGradient>
            <linearGradient id="tbl1" x1="227.788" y1="127.636" x2="227.788" y2="148.35" gradientUnits="userSpaceOnUse">
              <stop stop-color="#49CAE5"/>
              <stop offset="1" stop-color="#3E68B8"/>
            </linearGradient>
            <linearGradient id="tbl2" x1="21.8786" y1="77.6055" x2="21.8786" y2="111.206" gradientUnits="userSpaceOnUse">
              <stop stop-color="#F79A4D"/>
              <stop offset="1" stop-color="#F46251"/>
            </linearGradient>
          </defs>
        </svg>
        <span class="doc-name">${d.currentDocumentName}</span>
        ${m.isDirty ? html`<ark-tooltip content="Unsaved changes" placement="bottom"><span class="dirty-dot"></span></ark-tooltip>` : nothing}

        <div class="separator"></div>

        ${this._renderParseStatus()}

        <div class="separator"></div>

        <sysml-view-selector
          .views=${m.availableViews as ViewDefinition[]}
          .activeViewId=${m.activeViewId}
          @view-change=${this._onViewChange}
        ></sysml-view-selector>
      </div>

      <div class="right">
        <!-- Diagram view type selector -->
        <div class="btn-group">
          <ark-tooltip content="Block Diagram" placement="bottom">
            <ark-button size="sm" variant="ghost" class=${this.diagramVisible && this.diagramView === 'diagram' ? 'tb-active' : ''} @click=${() => this._setDiagramView('diagram')}>
              <ark-icon name="box" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="Internal Block Diagram" placement="bottom">
            <ark-button size="sm" variant="ghost" class=${this.diagramVisible && this.diagramView === 'ibd' ? 'tb-active' : ''} @click=${() => this._setDiagramView('ibd')}>
              <ark-icon name="git-branch" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="Sequence Diagram" placement="bottom">
            <ark-button size="sm" variant="ghost" class=${this.diagramVisible && this.diagramView === 'sequence' ? 'tb-active' : ''} @click=${() => this._setDiagramView('sequence')}>
              <ark-icon name="arrow-left-right" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="State Machine Diagram" placement="bottom">
            <ark-button size="sm" variant="ghost" class=${this.diagramVisible && this.diagramView === 'statemachine' ? 'tb-active' : ''} @click=${() => this._setDiagramView('statemachine')}>
              <ark-icon name="git-compare" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="Activity Diagram" placement="bottom">
            <ark-button size="sm" variant="ghost" class=${this.diagramVisible && this.diagramView === 'activity' ? 'tb-active' : ''} @click=${() => this._setDiagramView('activity')}>
              <ark-icon name="router" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="Package Diagram" placement="bottom">
            <ark-button size="sm" variant="ghost" class=${this.diagramVisible && this.diagramView === 'package' ? 'tb-active' : ''} @click=${() => this._setDiagramView('package')}>
              <ark-icon name="package" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="Requirement Diagram" placement="bottom">
            <ark-button size="sm" variant="ghost" class=${this.diagramVisible && this.diagramView === 'requirement' ? 'tb-active' : ''} @click=${() => this._setDiagramView('requirement')}>
              <ark-icon name="list-checks" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="Use Case Diagram" placement="bottom">
            <ark-button size="sm" variant="ghost" class=${this.diagramVisible && this.diagramView === 'usecase' ? 'tb-active' : ''} @click=${() => this._setDiagramView('usecase')}>
              <ark-icon name="user" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="Parametric Diagram" placement="bottom">
            <ark-button size="sm" variant="ghost" class=${this.diagramVisible && this.diagramView === 'parametric' ? 'tb-active' : ''} @click=${() => this._setDiagramView('parametric')}>
              <ark-icon name="sliders" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="Auto Layout" placement="bottom">
            <ark-button size="sm" variant="ghost" @click=${this._onAutoLayout}>
              <ark-icon name="grid" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
        </div>

        <div class="separator"></div>

        <!-- Layout controls -->
        <div class="btn-group">
          <ark-tooltip content="Layout Algorithm" placement="bottom">
            <select
              class="layout-select"
              .value=${m.layoutOptions.algorithm}
              @change=${(e: Event) => this._onLayoutAlgorithmChange((e.target as HTMLSelectElement).value)}
            >
              <option value="layered">Layered</option>
              <option value="stress">Stress</option>
              <option value="mrtree">Tree</option>
              <option value="force">Force</option>
            </select>
          </ark-tooltip>
          <ark-tooltip content=${`Direction: ${m.layoutOptions.direction}`} placement="bottom">
            <ark-button size="sm" variant="ghost" @click=${this._onToggleDirection}>
              <ark-icon name=${m.layoutOptions.direction === 'RIGHT' ? 'arrow-right' : 'arrow-down'} size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
        </div>

        <div class="separator"></div>

        <!-- Edit tools -->
        <div class="btn-group">
          <ark-tooltip content="Format Source" placement="bottom">
            <ark-button size="sm" variant="ghost" @click=${this._onFormat}>
              <ark-icon name="file-text" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content=${this._copiedFeedback ? 'Copied!' : 'Copy to Clipboard'} placement="bottom">
            <ark-button size="sm" variant="ghost" @click=${this._onCopy}>
              <ark-icon name=${this._copiedFeedback ? 'check' : 'copy'} size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <ark-tooltip content="Download .sysml File" placement="bottom">
            <ark-button size="sm" variant="ghost" @click=${this._onDownload}>
              <ark-icon name="download" size="sm"></ark-icon>
            </ark-button>
          </ark-tooltip>
          <!-- Export diagram -->
          <div class="export-menu">
            <ark-tooltip content="Export Diagram" placement="bottom">
              <ark-button size="sm" variant="ghost" @click=${this._toggleExportMenu}>
                <ark-icon name="external-link" size="sm"></ark-icon>
              </ark-button>
            </ark-tooltip>
            ${this._exportMenuOpen
              ? html`
                  <div class="export-dropdown">
                    <button class="export-dropdown-item" @click=${() => this._onExportDiagram('svg')}>
                      <ark-icon name="file-text" size="sm"></ark-icon>
                      Export SVG
                    </button>
                    <button class="export-dropdown-item" @click=${() => this._onExportDiagram('png')}>
                      <ark-icon name="file-text" size="sm"></ark-icon>
                      Export PNG
                    </button>
                  </div>
                `
              : nothing}
          </div>
        </div>

        <div class="separator"></div>

        <ark-tooltip content="Save  ${MOD}+S" placement="bottom">
          <ark-button
            size="sm"
            variant="primary"
            ?disabled=${!m.isDirty || d.isSaving}
            @click=${this._onSave}
          >
            <ark-icon name="save" size="sm"></ark-icon>
            ${d.isSaving ? 'Saving...' : 'Save'}
          </ark-button>
        </ark-tooltip>

      </div>
    `;
  }

  private _renderParseStatus() {
    const m = this.modeler;

    if (m.isParsing) {
      return html`<span class="badge badge-info"><ark-spinner size="xs"></ark-spinner> Parsing...</span>`;
    }
    if (m.parseSuccess) {
      return html`<span class="badge badge-success">${m.blockCount} blocks</span>`;
    }
    if (m.errorCount > 0) {
      return html`<span class="badge badge-error">${m.errorCount} error${m.errorCount !== 1 ? 's' : ''}</span>`;
    }
    return html`<span class="badge badge-neutral">No model</span>`;
  }

  private _onFormat(): void {
    this.dispatchEvent(new CustomEvent('format', { bubbles: true, composed: true }));
  }

  private _onAutoLayout(): void {
    this.dispatchEvent(new CustomEvent('auto-layout', { bubbles: true, composed: true }));
  }

  private async _onCopy(): Promise<void> {
    await navigator.clipboard.writeText(this.modeler.sourceText);
    this._copiedFeedback = true;
    setTimeout(() => { this._copiedFeedback = false; }, 2000);
  }

  private _onDownload(): void {
    const blob = new Blob([this.modeler.sourceText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'model.sysml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private _setDiagramView(view: 'diagram' | 'ibd' | 'sequence' | 'statemachine' | 'activity' | 'package' | 'requirement' | 'usecase' | 'parametric') {
    this.dispatchEvent(new CustomEvent('diagram-view-change', {
      detail: view,
      bubbles: true,
      composed: true,
    }));
  }

  private _onViewChange(e: CustomEvent<{ viewId: string | null }>): void {
    this.modeler.selectView(e.detail.viewId);
  }

  private _onSave(): void {
    this.dispatchEvent(new CustomEvent('save', { bubbles: true, composed: true }));
  }

  private _onLayoutAlgorithmChange(algorithm: string): void {
    this.dispatchEvent(new CustomEvent('layout-algorithm-change', {
      detail: { algorithm },
      bubbles: true,
      composed: true,
    }));
  }

  private _onToggleDirection(): void {
    const next = this.modeler.layoutOptions.direction === 'RIGHT' ? 'DOWN' : 'RIGHT';
    this.dispatchEvent(new CustomEvent('layout-direction-change', {
      detail: { direction: next },
      bubbles: true,
      composed: true,
    }));
  }

  private _toggleExportMenu(): void {
    this._exportMenuOpen = !this._exportMenuOpen;
    if (this._exportMenuOpen) {
      // Close on next click outside
      const close = () => {
        this._exportMenuOpen = false;
        document.removeEventListener('click', close);
      };
      // Defer to avoid the current click triggering the close
      requestAnimationFrame(() => document.addEventListener('click', close));
    }
  }

  private _onExportDiagram(format: 'svg' | 'png'): void {
    this._exportMenuOpen = false;
    this.dispatchEvent(new CustomEvent('diagram-export', {
      detail: { format },
      bubbles: true,
      composed: true,
    }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-toolbar': SysmlToolbar;
  }
}
